import styled from "styled-components";
import type { GlobalToken } from "antd";
import { radius } from "@libertify/ui";

export interface TokenProps {
  $token: GlobalToken;
}

export const Sidebar = styled.aside<TokenProps>`
  display: flex;
  flex-direction: column;
  width: 32%;
  min-width: 320px;
  max-width: 440px;
  flex-shrink: 0;
  padding: 40px 32px 24px 40px;
  overflow-y: auto;

  @media (width <= 1000px) {
    width: 100%;
    min-width: 0;
    max-width: none;
    border-bottom: 1px solid ${({ $token }) => $token.colorBorderSecondary};
  }
`;

export const StepEyebrow = styled.div<TokenProps>`
  font-size: ${({ $token }) => $token.fontSizeSM}px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ $token }) => $token.colorPrimary};
  margin-bottom: 8px;
`;

export const Title = styled.h2<TokenProps>`
  font-size: ${({ $token }) => $token.fontSizeHeading4}px;
  font-weight: 600;
  letter-spacing: -0.03em;
  color: ${({ $token }) => $token.colorText};
  margin: 0 0 20px;
`;

export const Dropzone = styled.div<TokenProps & { $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  min-height: 240px;
  padding: 32px;
  border-radius: ${radius.box}px;
  cursor: pointer;
  color: ${({ $token, $active }) =>
    $active ? $token.colorPrimary : $token.colorTextTertiary};
  border: 1.5px dashed
    ${({ $token, $active }) =>
      $active ? $token.colorPrimary : $token.colorBorder};
  background: ${({ $token, $active }) =>
    $active ? $token.colorPrimaryBg : $token.colorBgContainer};
  transition: all ${({ $token }) => $token.motionDurationMid};

  &:hover {
    border-color: ${({ $token }) => $token.colorPrimary};
  }
`;

export const DropTitle = styled.div<TokenProps>`
  font-size: ${({ $token }) => $token.fontSizeLG}px;
  font-weight: 600;
  color: ${({ $token }) => $token.colorText};
`;

export const DropSubtitle = styled.div<TokenProps>`
  font-size: ${({ $token }) => $token.fontSize}px;
  color: ${({ $token }) => $token.colorTextSecondary};
  max-width: 280px;
  line-height: 1.4;
`;

export const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// Row wrapper: the order number sits OUTSIDE the card, to its left.
export const FileRowWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;


export const FileRow = styled.div<TokenProps & { $dragOver: boolean }>`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: ${radius.box}px;
  background: ${({ $token }) => $token.colorBgContainer};
  border: 1px solid
    ${({ $token, $dragOver }) =>
      $dragOver ? $token.colorPrimary : $token.colorBorderSecondary};
  box-shadow: ${({ $token }) => $token.boxShadowTertiary};
`;

export const DragHandle = styled.span<TokenProps>`
  display: inline-flex;
  color: ${({ $token }) => $token.colorTextQuaternary};
  cursor: grab;
  flex-shrink: 0;
`;

export const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const FileName = styled.div<TokenProps>`
  font-size: ${({ $token }) => $token.fontSize}px;
  font-weight: 500;
  color: ${({ $token }) => $token.colorText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const FileMeta = styled.div<TokenProps>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ $token }) => $token.fontSizeSM}px;
  color: ${({ $token }) => $token.colorTextTertiary};
`;

// File-type badge. Colors are per document type (no theme token exists for
// "PDF red" etc.) — kept as a small local map.
export const ExtTag = styled.span<{ $color: string; $bg: string }>`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
`;

export const IconBtn = styled.button<TokenProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border: none;
  border-radius: ${radius.circle};
  cursor: pointer;
  background: transparent;
  color: ${({ $token }) => $token.colorTextTertiary};
  transition: all ${({ $token }) => $token.motionDurationFast};

  &:hover {
    background: ${({ $token }) => $token.colorFillSecondary};
    color: ${({ $token }) => $token.colorText};
  }
`;

// Visibility (eye) toggle. When a file is marked hidden it becomes a highlighted
// rounded square in the theme's warning color, flagging its "knowledge-base
// only" state.
export const VisibilityToggle = styled.button<
  TokenProps & { $hidden: boolean }
>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: none;
  cursor: pointer;
  border-radius: ${({ $hidden }) => ($hidden ? "8px" : radius.circle)};
  background: ${({ $token, $hidden }) =>
    $hidden ? $token.colorWarning : "transparent"};
  color: ${({ $token, $hidden }) =>
    $hidden ? $token.colorWhite : $token.colorTextTertiary};
  transition: all ${({ $token }) => $token.motionDurationFast};

  &:hover {
    background: ${({ $token, $hidden }) =>
      $hidden ? $token.colorWarning : $token.colorFillSecondary};
    color: ${({ $token, $hidden }) =>
      $hidden ? $token.colorWhite : $token.colorText};
  }
`;

// Full-width "add more files" affordance below the list — a transparent,
// dashed-border dropzone-style box.
export const AddMoreBtn = styled.button<TokenProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 88px;
  margin-top: 12px;
  padding: 24px;
  border-radius: ${radius.box}px;
  cursor: pointer;
  background: transparent;
  border: 1.5px dashed ${({ $token }) => $token.colorBorder};
  color: ${({ $token }) => $token.colorTextSecondary};
  font-size: ${({ $token }) => $token.fontSize}px;
  font-weight: 600;
  transition: all ${({ $token }) => $token.motionDurationFast};

  &:hover {
    border-color: ${({ $token }) => $token.colorPrimary};
    color: ${({ $token }) => $token.colorPrimary};
    background: ${({ $token }) => $token.colorPrimaryBg};
  }
`;

export const Note = styled.p<TokenProps>`
  margin: 16px 0 0;
  font-size: ${({ $token }) => $token.fontSizeSM}px;
  color: ${({ $token }) => $token.colorTextTertiary};
  line-height: 1.4;
`;
